import React from 'react';
import { Typography, Descriptions, Alert } from 'antd';

export type AnnualLeaveBreakdown = {
  bfPrevious: number | null;
  entitlement: number | null;
  accumulatedToStart: number;
  balanceStart: number | null;
  takenMonth: number;
  balanceEnd: number | null;
};

export type OtherLeaveTakenEntry = {
  label: string;
  daysMonth: number;
  daysCumulative: number;
};

export type LeaveSummary = {
  contractPeriod: string | null;
  annual: AnnualLeaveBreakdown;
  sickTakenMonth: number | null;
  sickTakenCumulative: number;
  otherLeaveTaken: OtherLeaveTakenEntry[];
};

type Props = {
  leaveSummary: LeaveSummary | null;
  mode: 'profile' | 'timesheet';
};

const SICK_LEAVE_FULL_PAY_THRESHOLD_DAYS = 15;
const UNPAID_LEAVE_LABEL = 'Unpaid';
const UNPAID_LEAVE_ROW_LABEL = 'Unpaid Leave taken during the month';
const SICK_LEAVE_WARNING_TEXT =
  "NOTICE: As of today's date, the employee has exhausted the initial 15 days of full paid sick leave entitlement under UAE Labour Law; any additional sick leave will be processed in accordance with the applicable sick leave provisions and company policies.";

const formatDays = (n: number | null): string => (n === null ? '—' : n.toFixed(2));

const pluralizeDays = (n: number): string => {
  if (n === 1) return '1 day';
  if (n === 0) return '0 days';
  return `${n.toFixed(2)} days`;
};

const LeaveDetailsPanel: React.FC<Props> = ({ leaveSummary, mode }) => {
  if (!leaveSummary) {
    return null;
  }
  const { annual, sickTakenMonth, sickTakenCumulative, otherLeaveTaken } = leaveSummary;

  if (mode === 'profile') {
    const accumulated = annual.accumulatedToStart + annual.takenMonth;
    const showWarning = sickTakenCumulative >= SICK_LEAVE_FULL_PAY_THRESHOLD_DAYS;
    return (
      <div data-testid="leave-details-panel">
        <Typography.Title level={3} style={{ marginTop: '20px' }}>
          Leave Details
        </Typography.Title>
        {showWarning && (
          <Alert
            type="warning"
            showIcon
            message={SICK_LEAVE_WARNING_TEXT}
            style={{ marginBottom: 16 }}
            data-testid="sick-leave-warning"
          />
        )}
        <Descriptions layout="vertical" bordered column={2}>
          {annual.bfPrevious !== null && (
            <Descriptions.Item label="Brought forward from previous contract">
              {annual.bfPrevious.toFixed(2)}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Current contract allocation">
            {formatDays(annual.entitlement)}
          </Descriptions.Item>
          <Descriptions.Item label="Accumulated annual leave taken">
            {accumulated.toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Current balance">
            {formatDays(annual.balanceEnd)}
          </Descriptions.Item>
        </Descriptions>
      </div>
    );
  }

  // Unpaid leave is displayed as days taken in the current month (not cumulative since contract
  // start, unlike every other "other" leave type). It gets a distinct row label so the period is
  // unambiguous, and is filtered/hidden by its own monthly figure rather than the cumulative one.
  const otherRows = otherLeaveTaken.filter((e) =>
    e.label === UNPAID_LEAVE_LABEL ? e.daysMonth > 0 : e.daysCumulative > 0,
  );
  const hasSick = sickTakenMonth !== null && sickTakenMonth !== 0;
  const showOtherBlock = hasSick || otherRows.length > 0;

  return (
    <div data-testid="leave-details-panel">
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        Annual Leave Details
      </Typography.Title>
      <Descriptions layout="vertical" bordered column={2}>
        {annual.bfPrevious !== null && (
          <Descriptions.Item label="Brought forward from previous contract">
            {annual.bfPrevious.toFixed(2)}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Current contract allocation">
          {formatDays(annual.entitlement)}
        </Descriptions.Item>
        <Descriptions.Item label="Accumulated annual leave taken to start of month">
          {annual.accumulatedToStart.toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Annual leave balance at the start of the month">
          {formatDays(annual.balanceStart)}
        </Descriptions.Item>
        <Descriptions.Item label="Annual leave taken during the month">
          {annual.takenMonth.toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Annual leave balance at the end of the month">
          {formatDays(annual.balanceEnd)}
        </Descriptions.Item>
      </Descriptions>
      {showOtherBlock && (
        <>
          <Typography.Title level={4} style={{ marginTop: '20px' }}>
            Other Leaves Details
          </Typography.Title>
          <Descriptions layout="vertical" bordered column={2}>
            <Descriptions.Item label="Sick Leave taken during the month">
              {pluralizeDays(sickTakenMonth ?? 0)}
            </Descriptions.Item>
            {otherRows.map((entry) => {
              const isUnpaid = entry.label === UNPAID_LEAVE_LABEL;
              return (
                <Descriptions.Item
                  key={entry.label}
                  label={isUnpaid ? UNPAID_LEAVE_ROW_LABEL : entry.label}
                >
                  {pluralizeDays(isUnpaid ? entry.daysMonth : entry.daysCumulative)}
                </Descriptions.Item>
              );
            })}
          </Descriptions>
        </>
      )}
    </div>
  );
};

export default LeaveDetailsPanel;
